import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  ChevronDown, 
  ChevronUp, 
  Plus, 
  Trash2, 
  Edit, 
  Search,
  Filter,
  Download,
  X
} from "lucide-react";
import { Guest, guests as allGuests, getStatusLabel } from "@/data/mockData";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";

const Manager = () => {
  const [guests, setGuests] = useState<Guest[]>(allGuests);
  const [selectedGuests, setSelectedGuests] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Guest | "";
    direction: "ascending" | "descending";
  }>({ key: "", direction: "ascending" });

  const filteredGuests = useMemo(() => {
    return guests.filter(guest => 
      guest.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guest.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guest.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guest.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (guest.topics && guest.topics.some(topic => topic.toLowerCase().includes(searchTerm.toLowerCase())))
    );
  }, [guests, searchTerm]);

  const sortedGuests = useMemo(() => {
    const sortableGuests = [...filteredGuests];
    if (sortConfig.key !== "") {
      sortableGuests.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableGuests;
  }, [filteredGuests, sortConfig]);

  const requestSort = (key: keyof Guest) => {
    let direction: "ascending" | "descending" = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const handleSelectAll = () => {
    if (selectedGuests.length === sortedGuests.length) {
      setSelectedGuests([]);
    } else {
      setSelectedGuests(sortedGuests.map(guest => guest.id));
    }
  };

  const handleSelectGuest = (id: string) => {
    if (selectedGuests.includes(id)) {
      setSelectedGuests(selectedGuests.filter(guestId => guestId !== id));
    } else {
      setSelectedGuests([...selectedGuests, id]);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedGuests.length === 0) return;
    
    const newGuests = guests.filter(guest => !selectedGuests.includes(guest.id));
    setGuests(newGuests);
    setSelectedGuests([]);
    
    toast({
      title: "Success",
      description: `Deleted ${selectedGuests.length} guests successfully`,
    });
  };

  const handleAddNewGuest = () => {
    toast({
      title: "Add New Guest",
      description: "This would open a form to add a new guest in a real application",
    });
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSortConfig({ key: "", direction: "ascending" });
  };

  const getSortDirectionIcon = (key: keyof Guest) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "ascending" ? 
      <ChevronUp className="h-4 w-4" /> : 
      <ChevronDown className="h-4 w-4" />;
  };

  return (
    <main className="container mx-auto py-6 px-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Guest Manager</h1>
        <div className="flex space-x-2">
          <Button onClick={handleAddNewGuest} variant="default">
            <Plus className="mr-2 h-4 w-4" /> Add Guest
          </Button>
          <Button 
            onClick={handleDeleteSelected} 
            variant="destructive" 
            disabled={selectedGuests.length === 0}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete ({selectedGuests.length})
          </Button>
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" /> Export
          </Button>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4 gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search guests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
          {searchTerm && (
            <Button 
              variant="ghost" 
              onClick={() => setSearchTerm("")}
              className="absolute right-2 top-2 h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-10">
            <Filter className="mr-2 h-4 w-4" /> Filter
          </Button>
          {(searchTerm || sortConfig.key !== "") && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearFilters}
              className="h-10"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">
                <Checkbox 
                  checked={selectedGuests.length === sortedGuests.length && sortedGuests.length > 0}
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all"
                />
              </TableHead>
              <TableHead
                className="cursor-pointer"
                onClick={() => requestSort("name")}
              >
                <div className="flex items-center justify-between">
                  Guest
                  {getSortDirectionIcon("name")}
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer hidden md:table-cell"
                onClick={() => requestSort("title")}
              >
                <div className="flex items-center justify-between">
                  Title/Company
                  {getSortDirectionIcon("title")}
                </div>
              </TableHead>
              <TableHead className="hidden lg:table-cell">Topics</TableHead>
              <TableHead
                className="cursor-pointer"
                onClick={() => requestSort("status")}
              >
                <div className="flex items-center justify-between">
                  Status
                  {getSortDirectionIcon("status")}
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer hidden md:table-cell"
                onClick={() => requestSort("scheduledDate")}
              >
                <div className="flex items-center justify-between">
                  Scheduled
                  {getSortDirectionIcon("scheduledDate")}
                </div>
              </TableHead>
              <TableHead className="w-[100px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedGuests.length > 0 ? (
              sortedGuests.map(guest => (
                <TableRow key={guest.id} className="group">
                  <TableCell>
                    <Checkbox 
                      checked={selectedGuests.includes(guest.id)}
                      onCheckedChange={() => handleSelectGuest(guest.id)}
                      aria-label={`Select ${guest.name}`}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={guest.imageUrl} alt={guest.name} />
                        <AvatarFallback>
                          {guest.name.split(" ").map(n => n[0]).join("")}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{guest.name}</div>
                        <div className="text-sm text-muted-foreground md:hidden">
                          {guest.title}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div>
                      <div className="font-medium">{guest.title}</div>
                      <div className="text-sm text-muted-foreground">{guest.company}</div>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[200px] hidden lg:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {guest.topics.slice(0, 2).map((topic, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {topic}
                        </Badge>
                      ))}
                      {guest.topics.length > 2 && (
                        <Badge variant="outline" className="text-xs">
                          +{guest.topics.length - 2}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`bg-status-${guest.status}`}>
                      {getStatusLabel(guest.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {guest.scheduledDate ? new Date(guest.scheduledDate).toLocaleDateString() : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-8 w-8"
                        asChild
                      >
                        <Link to={`/guest/${guest.id}`}>
                          <Edit className="h-4 w-4" />
                          <span className="sr-only">Edit</span>
                        </Link>
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => {
                          setGuests(guests.filter(g => g.id !== guest.id));
                          toast({
                            title: "Guest deleted",
                            description: `${guest.name} has been removed`
                          });
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Delete</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center">
                  No guests found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </main>
  );
};

export default Manager;
